import React, { useCallback, useState, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import BootstrapModalWrapper from "components/bootstrap/BootstrapModalWrapper";
import { Modal, Alert } from "components/bootstrap";
import { IfPermitted, ModalSubmit } from "components/common";
import DecoratorList from "views/components/messagelist/decorators/DecoratorList";
import AddDecoratorButton from "views/components/messagelist/decorators/AddDecoratorButton";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import StreamSelect, { DEFAULT_SEARCH_ID, DEFAULT_STREAM_ID } from "./StreamSelect";
import formatDecorator from "./FormatDecorator";
const _updateOrder = (orderedDecorators, decorators, onChange) => {
  const newDecorators = cloneDeep(decorators);
  orderedDecorators.forEach((item, idx) => {
    const decorator = newDecorators.find((i) => i.id === item.id);
    if (decorator) {
      decorator.order = idx;
    }
  });
  onChange(newDecorators);
};
const DecoratorsConfigUpdate = ({ streams, decorators, types, show = false, onCancel, onSave }) => {
  const [currentStream, setCurrentStream] = useState(DEFAULT_STREAM_ID);
  const [modifiedDecorators, setModifiedDecorators] = useState(decorators);
  const sendTelemetry = useSendTelemetry("decorators");
  const { permissions } = useCurrentUser();
  const canEditStream = useMemo(
    () => isPermitted(permissions, `streams:edit:${currentStream}`) || currentStream === DEFAULT_SEARCH_ID,
    [currentStream, permissions]
  );
  const onCreate = useCallback(
    ({ stream, ...rest }) => setModifiedDecorators([
      ...modifiedDecorators,
      {
        ...rest,
        stream: stream === DEFAULT_SEARCH_ID ? null : stream
      }
    ]),
    [modifiedDecorators, setModifiedDecorators]
  );
  const onReorder = useCallback(
    (orderedDecorators) => _updateOrder(orderedDecorators, modifiedDecorators, setModifiedDecorators),
    [modifiedDecorators, setModifiedDecorators]
  );
  const onSubmit = useCallback(() => {
    onSave(modifiedDecorators);
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.DECORATORS_UPDATED, {
      app_action_value: "configuration-save"
    });
  }, [onSave, modifiedDecorators, sendTelemetry]);
  const currentDecorators = modifiedDecorators.filter(
    (decorator) => (decorator.stream || DEFAULT_SEARCH_ID) === currentStream
  );
  const decoratorItems = currentDecorators.sort((d1, d2) => d1.order - d2.order).map(
    (decorator) => formatDecorator(decorator, modifiedDecorators, types, canEditStream ? setModifiedDecorators : void 0)
  );
  const nextOrder = currentDecorators.reduce((currentMax, decorator) => Math.max(currentMax, decorator.order), 0) + 1;
  const _onCancel = useCallback(() => {
    setModifiedDecorators(decorators);
    onCancel();
  }, [decorators, onCancel]);
  const modalTitle = "Update Default Decorators Configuration";
  return /* @__PURE__ */ React.createElement(BootstrapModalWrapper, { showModal: show, onHide: _onCancel }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement("p", null, "Select the stream for which you want to change the set of default decorators."), /* @__PURE__ */ React.createElement(StreamSelect, { onChange: setCurrentStream, value: currentStream, streams }), !canEditStream && currentStream && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Read only access - you need permission to edit the selected stream"), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "decorators:create" }, /* @__PURE__ */ React.createElement("p", null, "Select the type to create a new decorator for this stream:"), /* @__PURE__ */ React.createElement(
    AddDecoratorButton,
    {
      stream: currentStream,
      nextOrder,
      decoratorTypes: types,
      onCreate,
      showHelp: false,
      disabled: !canEditStream
    }
  )), /* @__PURE__ */ React.createElement("p", null, "Use drag and drop to change the execution order of the decorators."), /* @__PURE__ */ React.createElement(DecoratorList, { decorators: decoratorItems, onReorder })), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onSubmit,
      onCancel: _onCancel,
      submitButtonText: "Update configuration",
      disabledSubmit: !canEditStream
    }
  )));
};
export default DecoratorsConfigUpdate;
